(ns bp-clients.tv-app.common.components.vertical-scroll-with-lazy-rendering
  (:require
   ["react" :refer [createRef]]
   [bp-clients.tv-app.common.components.carousel :refer [carousel-3d]]
   [bp-clients.tv-app.common.components.highlights :refer [highlights]]
   [bp-clients.tv-app.common.components.thumb :as thumb-components]
   [bp-clients.tv-app.nav.events :as nav-events]
   [bp-clients.tv-app.utils.keycodes :as keycodes]
   [bp-clients.tv-app.utils.utils :as utils]
   [bp-libs.design-system.components.grid :refer [grid]]
   [goog.functions :refer [debounce]]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def ^:private time-to-change-banner
  "This value is used for banner transition debounce."
  750)

(def on-thumb-focus
  (debounce (fn [ignore-navigation? item]
              (reset! ignore-navigation? false)
              (rf/dispatch [::nav-events/set-active-item item])
              (rf/dispatch [::nav-events/set-banner-transition-in-progress false]))
            time-to-change-banner))

(defn- carousel-gen
  [{:keys [items]} events ignore-navigation?]
  (let [items (filter #(-> % :thumbnail :image seq) items)]
    (mapv #(thumb-components/carousel-thumb (merge events % {:on-focus (partial on-thumb-focus ignore-navigation?)})) items)))

(defn- gen-grid-items
  [{:keys [items]} events ignore-navigation?]
  (let [items (filter #(-> % :thumbnail :image seq) items)]
    (mapv #(thumb-components/grid-thumb (merge events % {:on-focus (partial on-thumb-focus ignore-navigation?)})) items)))

(defn- gen-highlight-items
  "focus-down-selector is used to force navigation down to a specific
  node.

  This is used in highlights thumbs to avoid bug vertical navigation
  when navigating down from last highlight item to the next carousel
  (when it is fully scrolled to the left).

  To know more, read:
  https://github.com/luke-chang/js-spatial-navigation?tab=readme-ov-file#custom-attributes"
  [{:keys [items]} events ignore-navigation?]
  (let [items (filter #(-> % :thumbnail :image seq) items)]
    (mapv #(thumb-components/highlight-thumb (merge events % {:on-focus (partial on-thumb-focus ignore-navigation?)
                                                              :focus-down-selector "*.focusable-component:nth-of-type(2) .focusable-thumb"}))
          items)))

(def ^:private components-padding-top-class
  "pt-4")

(def ^:private components-padding-bottom-class
  "pb-4")

(defn- carousel-with-title
  [title thumbs {:keys [thumb-size id]} ignore-navigation?]
  (let [calculate-step  #(str (+ (js/parseFloat %) 0.933) "rem")
        ref             (createRef)
        block-ref       (createRef)
        title-component [:div {:class "text-white text-2xl font-medium pl-2 pb-1"
                               :ref   block-ref} title]
        thumb-width     (:width (get thumb-components/aspect-ratio->container thumb-size "16:9"))]
    (fn []
      (when (seq thumbs)
        [:div {:class ["focusable-component"
                       components-padding-bottom-class
                       components-padding-top-class]
               :key   id}
         [:div
          title-component
          [carousel-3d {:thumbs             thumbs
                        :on-navigate        #(rf/dispatch [::nav-events/set-banner-transition-in-progress true])
                        :ignore-navigation? ignore-navigation?
                        :step-3d            (calculate-step thumb-width)
                        :react-ref          ref}]]]))))

(defn- grid-with-state
  [thumbs {:keys [_in-view _id title columns items-classes ignore-navigation? horizontal-pos vertical-pos]}]
  (let [ref (createRef)
        ignore-navigation? (or ignore-navigation? (r/atom false))
        on-navigate        #(rf/dispatch [::nav-events/set-banner-transition-in-progress true])]
    (fn []
      (let [key-down-handler #(let [key-code (-> % .-keyCode)
                                    next-line-selector       (str "*.focusable-component:nth-of-type(" (inc @vertical-pos) ") .focusable-thumb")
                                    next-line-elements-count (.-length (.querySelectorAll (-> ref .-current .-parentElement)
                                                                                          next-line-selector))]
                                (cond
                                  @ignore-navigation?
                                  (utils/stop-event-propagation %)

                                  (keycodes/right? key-code)
                                  (if (> next-line-elements-count (inc @horizontal-pos))
                                    (do (reset! horizontal-pos (inc @horizontal-pos))
                                        (when on-navigate (on-navigate)))

                                    (do (reset! horizontal-pos (dec next-line-elements-count))
                                        (utils/stop-event-propagation %)))

                                  (keycodes/left? key-code)
                                  (do (reset! horizontal-pos (max 0 (dec @horizontal-pos)))
                                      (when on-navigate (on-navigate)))))]
        [:<>
         (map-indexed
          (fn [idx line]
            ^{:key (str "grid-" (hash line))}
            [grid {:items              line
                   :line-classes       ["focusable-component pl-2"]
                   :items-classes      items-classes
                   :columns            columns
                   :title              (when-not (pos? idx) title)
                   :ignore-navigation? ignore-navigation?
                   :on-key-down        key-down-handler
                   :react-ref          ref}])
          (partition columns columns nil thumbs))]))))

(defn- gen-keep-watching
  [items on-item-click ignore-navigation?]
  (doall
   (for [item items]
     ^{:key (:id item)} [thumb-components/keep-watching (merge item on-item-click
                                                               {:on-focus (partial on-thumb-focus ignore-navigation?)})])))

(defn- on-thumb-click
  [thumb]
  (if (= "tv_app_go_to_paywall_page" (-> thumb :event :__typename))
    (rf/dispatch [::nav-events/open-paywall (:event thumb)])
    (do (rf/dispatch [::nav-events/set-active-item thumb])
        (rf/dispatch [::nav-events/navigate-to-page]))))

(defn- current-component-height [active-component-el]
  (-> active-component-el
      .getBoundingClientRect
      .-height))

(defn- scroll-component [direction previous-vertical-pos vertical-pos focusable-components]
  ;; get focused element when is grid, instead of focusable-element
  (let [scroll-component (if (= :up @direction)
                           (get @focusable-components @vertical-pos)
                           (get @focusable-components @previous-vertical-pos))]
    scroll-component))

(defn- get-focusable-components [react-ref]
  (vec (.querySelectorAll (.-current react-ref) "*.focusable-component")))

(defn- update-scroll
  [react-ref vertical-pos previous-vertical-pos scroll-translate-px scroll-direction focusable-components]
  (when (.-current react-ref)
    (when (nil? @focusable-components)
      (reset! focusable-components (get-focusable-components react-ref)))

    (let [scroll-component-el (scroll-component scroll-direction previous-vertical-pos vertical-pos focusable-components)
          new-scroll-value (if (= :up @scroll-direction)
                             (+ @scroll-translate-px
                                (current-component-height scroll-component-el))
                             (- @scroll-translate-px
                                (current-component-height scroll-component-el)))]
      (js/console.log "; selected-carousel-index:" @vertical-pos
                      "; previous-component:" @previous-vertical-pos
                      "; total-components:" (count @focusable-components)
                      "; altura:" (current-component-height scroll-component-el)
                      "; dir:" (clj->js @scroll-direction)
                      "; current-scroll:" @scroll-translate-px
                      "; new-scroll:" new-scroll-value)
      (reset! scroll-translate-px new-scroll-value)
      (set! (-> react-ref .-current .-style .-transform) (str "translate3d(0, " new-scroll-value "px, 0)")))))

(defn- grids-total-vertical-lines [grid-components]
  (let [grids-lines (map #(js/Math.ceil (/ (-> % :items count) (:columns %))) grid-components)
        total-lines (apply + grids-lines)]
    total-lines))

(defn- components-data->elements [components thumb-configs ignore-navigation? horizontal-pos vertical-pos]
  #_{:clj-kondo/ignore [:used-underscored-binding]}
  (doall
   (for [{:keys [index title items columns __typename] :as carousel-data} components
         :let     [count-pos?  (comp pos? count)
                   not-empty? (count-pos? items)]
         :when not-empty?]

     (case __typename
       "tv_app_highlights"
       ^{:key (str "highlights-" index)}
       [:div {:class ["focusable-component"
                      components-padding-bottom-class
                      components-padding-top-class]}
        [highlights (gen-highlight-items carousel-data thumb-configs ignore-navigation?)
         ignore-navigation?
         #(rf/dispatch [::nav-events/set-banner-transition-in-progress true])]]

       "tv_app_grid"
       ^{:key (str "grid-" index)}
       [grid-with-state (gen-grid-items carousel-data thumb-configs ignore-navigation?)
        {:id                 title
         :title              title
         :items-classes      [components-padding-top-class components-padding-bottom-class "pr-2 pl-1"]
         :columns            columns
         :ignore-navigation? ignore-navigation?
         :horizontal-pos     horizontal-pos
         :vertical-pos       vertical-pos}]

       "tv_app_carousel"
       ^{:key (str "carousel-" index)}
       [carousel-with-title
        title
        (carousel-gen carousel-data thumb-configs ignore-navigation?)
        {:thumb-size (-> items first :thumbnail :aspect_ratio)
         :id title}
        ignore-navigation?]

       "keep_watching"
       ^{:key "keep-watching"}
       [carousel-with-title "Continuar assistindo" (gen-keep-watching items thumb-configs ignore-navigation?) {:id "Continuar assistindo" :thumb-size "16:9"} ignore-navigation?]

       [:<>]))))

(defn- vertical-scroll-elements
  [current-page components-per-page components-elements]
  [:div {:class ["last:mb-60"]}
   (take (* @current-page components-per-page) components-elements)])

(defn vertical-scroll-with-lazy-rendering
  [{:keys [components autofocus?]}]
  (let [scroll-div-ref                 (createRef)
        ignore-navigation?             (r/atom false)
        horizontal-pos                 (r/atom 0)
        vertical-pos                   (r/atom 0)
        thumb-configs                  {:on-click on-thumb-click
                                        :tab-index 10}
        components-elements            (components-data->elements components
                                                                  thumb-configs
                                                                  ignore-navigation?
                                                                  horizontal-pos
                                                                  vertical-pos)
        previous-vertical-pos          (r/atom 0)
        scroll-translate-py            (r/atom 0)
        scroll-direction               (r/atom nil)
        focusable-components           (r/atom nil)
        components-per-page            25
        current-page                   (r/atom 1)
        set-scroll                     (partial update-scroll scroll-div-ref vertical-pos previous-vertical-pos scroll-translate-py scroll-direction focusable-components)
        scroll-to-carousel             (fn [current-carousel-index direction]
                                         (reset! ignore-navigation? true)
                                         (reset! previous-vertical-pos @vertical-pos)
                                         (reset! vertical-pos current-carousel-index)
                                         (reset! scroll-direction direction)
                                         (let [loaded-pages (-> @vertical-pos inc (/ components-per-page) int inc)]
                                           (reset! current-page loaded-pages)
                                           (reset! focusable-components (get-focusable-components scroll-div-ref)))
                                         (set-scroll))
        grids                           (filter #(= "tv_app_grid" (:__typename %)) components)
        focusable-vertical-count        (+ (- (count components) (count grids))
                                           (grids-total-vertical-lines grids))]

    (r/create-class {:display-name "page-list-vertical-scroll"
                     :component-did-mount (fn [_react-component]
                                            (let [first-item (js/document.querySelector ".focusable-thumb")]
                                              (when (and autofocus? first-item)
                                                (.focus first-item))))

                     :reagent-render (fn [_props]
                                       [:div {:class ["duration-200 transition-transform"
                                                      "translate-y"
                                                      "transform-gpu will-change-transform scroll-py-0"]
                                              :style {:transform "translate3d(0,0,0)"}
                                              :on-transition-end (fn [e]
                                                                   ;; Attention: This event can be canceled by the browser
                                                                   ;; we should always consider this fact.
                                                                   ;; This logic counts that at least the last transition
                                                                   ;; of each sequence will not be canceled.
                                                                   ;; Empiracally this is what seems to happen.
                                                                   ;; on-transition-cancel is supported only in newer browsers
                                                                   ;; https://developer.mozilla.org/en-US/docs/Web/API/Element/animationcancel_event#browser_compatibility
                                                                   (let [actual-element-selector (str "*.focusable-component:nth-of-type(" (inc @vertical-pos) ") .focusable-thumb")
                                                                         line-elements           (vec (.querySelectorAll (.-current scroll-div-ref) actual-element-selector))
                                                                         focus-line-index        (-> line-elements count dec (min @horizontal-pos))]

                                                                     (when (-> e .-target .-classList set (contains? "translate-y"))
                                                                       (.focus (nth line-elements focus-line-index)
                                                                               (clj->js {:preventScroll true})))))
                                              :ref         scroll-div-ref
                                              :on-focus    #(reset! ignore-navigation? false)
                                              :on-key-down #(let [keyCode (-> % .-keyCode)]
                                                              (when (or (keycodes/up? keyCode) (keycodes/down? keyCode))
                                                                (rf/dispatch [::nav-events/set-banner-transition-in-progress true])
                                                                (utils/stop-event-propagation %)

                                                                (when-not @ignore-navigation?
                                                                  (cond
                                                                    (keycodes/up? keyCode)
                                                                    (when-not (zero? @vertical-pos)
                                                                      (reset! ignore-navigation? true)
                                                                      (scroll-to-carousel (max (dec @vertical-pos) 0) :up))

                                                                    (keycodes/down? keyCode)
                                                                    (when (> (dec focusable-vertical-count) @vertical-pos)
                                                                      (reset! ignore-navigation? true)
                                                                      (scroll-to-carousel (min (inc @vertical-pos) focusable-vertical-count) :down))

                                                                    :else nil))))}
                                        [vertical-scroll-elements current-page components-per-page components-elements]])})))
